import { useCallback, useEffect, useState } from "react";

import { type SupportedLocale } from "@/i18n/constants";
import {
  type Currency,
  resolveCurrencyFromCountry,
  resolveCurrencyFromLocale,
  resolveLocaleFromCountry,
} from "@/lib/geo-currency";

export type { Currency } from "@/lib/geo-currency";

// v2: invalida caches "v1" poluídos pela detecção antiga (server-side ipwho retornava o
// país do SERVIDOR Vercel — geralmente US — cobrando usuários BR em USD).
const GEO_CACHE_KEY = "gamefik-geo-cache-v2";

interface GeoCache {
  currency: Currency;
  source: string;
  timestamp: number;
}

export function detectCurrencyFromLocale(): Currency {
  if (typeof navigator === "undefined") return "brl";
  return resolveCurrencyFromLocale(navigator.language);
}

function getCache(): GeoCache | null {
  try {
    const raw = localStorage.getItem(GEO_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as GeoCache;
    if (!parsed.currency || !parsed.source || !parsed.timestamp) return null;
    return parsed;
  } catch {
    return null;
  }
}

function setCache(currency: Currency, source: string) {
  try {
    const cache: GeoCache = { currency, source, timestamp: Date.now() };
    localStorage.setItem(GEO_CACHE_KEY, JSON.stringify(cache));
  } catch {
    // ignore
  }
}

export function clearGeoCache() {
  try {
    localStorage.removeItem(GEO_CACHE_KEY);
    // Remove também o cache da versão antiga, para não reaproveitar dado poluído.
    localStorage.removeItem("gamefik-geo-cache-v1");
  } catch {
    // ignore
  }
}

export function getGeoCache(): GeoCache | null {
  return getCache();
}

interface GeoPricingState {
  currency: Currency;
  detectedLocale: SupportedLocale | null;
  isLoading: boolean;
  isCached: boolean;
  refresh: () => void;
}

/**
 * Resolve a moeda do usuário para o checkout.
 *
 * Ordem de prioridade (da mais confiável para a menos):
 *   1. Cache local (versão atual).
 *   2. `/api/geo` — header `x-vercel-ip-country` do edge da Vercel (IP REAL do usuário).
 *      Fonte AUTORITATIVA. (Antes era a última opção; agora é a primeira.)
 *   3. ipapi.co / ipinfo.io — chamadas do NAVEGADOR (veem o IP real do usuário).
 *   4. Locale do navegador — fallback fraco apenas se tudo acima falhar.
 *
 * Removida a antiga fonte `/api/ipwho`: ela chamava `ipwho.is` no SERVIDOR, então
 * geolocalizava o IP da função serverless (região US) em vez do usuário — fazendo
 * usuários do Brasil serem cobrados em USD e o pagamento falhar.
 */
export function useGeoPricing(): GeoPricingState {
  const [currency, setCurrency] = useState<Currency>(detectCurrencyFromLocale());
  const [detectedLocale, setDetectedLocale] = useState<SupportedLocale | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCached, setIsCached] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = useCallback(() => {
    clearGeoCache();
    setIsCached(false);
    setRefreshKey((k) => k + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const apply = (detected: Currency, countryCode: string | null | undefined, source: string) => {
      if (cancelled) return true;
      setCurrency(detected);
      const lang = resolveLocaleFromCountry(countryCode);
      if (lang) setDetectedLocale(lang as SupportedLocale);
      setIsCached(false);
      setCache(detected, source);
      setIsLoading(false);
      return true;
    };

    async function detect() {
      // 0. Cache local
      const cached = getCache();
      if (cached && !cancelled) {
        setCurrency(cached.currency);
        setIsCached(true);
        setIsLoading(false);
        return;
      }

      // 1. AUTORITATIVO: header de país do edge da Vercel (IP real do usuário).
      try {
        const res = await fetch("/api/geo", { signal: AbortSignal.timeout(3000) });
        if (res.ok) {
          const data = await res.json();
          if (data?.currency && apply(data.currency, data.country_code || data.country, "edge-api")) return;
        }
      } catch {
        // continua para fallback
      }

      // 2. Fallback: ipapi.co (chamado do navegador → IP do usuário)
      try {
        const res = await fetch("https://ipapi.co/json/", { signal: AbortSignal.timeout(3000) });
        if (res.ok) {
          const data = await res.json();
          const detected = resolveCurrencyFromCountry(data?.country_code);
          if (detected && apply(detected, data?.country_code, "ipapi.co")) return;
        }
      } catch {
        // continua para fallback
      }

      // 3. Fallback: ipinfo.io (chamado do navegador → IP do usuário)
      try {
        const res = await fetch("https://ipinfo.io/json", { signal: AbortSignal.timeout(3000) });
        if (res.ok) {
          const data = await res.json();
          const detected = resolveCurrencyFromCountry(data?.country);
          if (detected && apply(detected, data?.country, "ipinfo.io")) return;
        }
      } catch {
        // continua para fallback final
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    setIsLoading(true);
    detect();
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  return { currency, detectedLocale, isLoading, isCached, refresh };
}

export function formatPrice(amountInCents: number, currency: Currency): string {
  const amount = amountInCents / 100;
  switch (currency) {
    case "brl":
      return `R$ ${amount.toFixed(2).replace(".", ",")}`;
    case "eur":
      return `€ ${amount.toFixed(2).replace(".", ",")}`;
    case "usd":
      return `$ ${amount.toFixed(2)}`;
    default:
      return `R$ ${amount.toFixed(2).replace(".", ",")}`;
  }
}

export function formatPriceShort(amountInCents: number, currency: Currency): string {
  const amount = amountInCents / 100;
  const isWhole = amount % 1 === 0;
  switch (currency) {
    case "brl":
      return `R$ ${isWhole ? amount.toFixed(0) : amount.toFixed(2).replace(".", ",")}`;
    case "eur":
      return `€ ${isWhole ? amount.toFixed(0) : amount.toFixed(2).replace(".", ",")}`;
    case "usd":
      return `$ ${isWhole ? amount.toFixed(0) : amount.toFixed(2)}`;
    default:
      return `R$ ${isWhole ? amount.toFixed(0) : amount.toFixed(2).replace(".", ",")}`;
  }
}
